import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart2, ArrowLeft, ChevronDown, ChevronUp, BookOpen, CheckSquare } from 'lucide-react';
import { Button } from '../components/Button';
import { supabase } from '../lib/supabase';
import { format, startOfWeek, endOfWeek, subWeeks, isBefore, differenceInWeeks } from 'date-fns';
import { ProtectedLayout } from '../components/ProtectedLayout';

export function WeeklySummaries() {
  const navigate = useNavigate();
  const [summaries, setSummaries] = useState<any[]>([]);
  const [expandedWeek, setExpandedWeek] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [signupDate, setSignupDate] = useState<Date | null>(null);
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);

  const toggleWeek = (weekId: string) => {
    setExpandedWeek(expandedWeek === weekId ? null : weekId);
  };

  useEffect(() => {
    fetchSummaries();
  }, []);

  const fetchSummaries = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/');
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('created_at')
        .eq('id', session.user.id)
        .single();

      if (profileError) throw profileError;

      const userSignupDate = new Date(profile.created_at);
      setSignupDate(userSignupDate);

      const now = new Date();
      const weeksSinceSignup = Math.max(0, differenceInWeeks(now, userSignupDate));
      const numberOfWeeks = Math.min(weeksSinceSignup + 1, 12);

      const weeks = Array.from({ length: numberOfWeeks }, (_, i) => {
        const weekStart = startOfWeek(subWeeks(now, i));
        const weekEnd = endOfWeek(subWeeks(now, i));
        return { start: weekStart, end: weekEnd };
      });

      const weeklyData = await Promise.all(
        weeks.map(async ({ start, end }) => {
          const { data: journalEntries } = await supabase
            .from('journal_entries')
            .select('*')
            .eq('user_id', session.user.id)
            .gte('created_at', start.toISOString())
            .lte('created_at', end.toISOString());

          const { data: lessons } = await supabase
            .from('lessons_learned')
            .select('*')
            .eq('user_id', session.user.id)
            .gte('date', start.toISOString())
            .lte('date', end.toISOString());

          const { data: tasks } = await supabase
            .from('tasks')
            .select('*')
            .eq('user_id', session.user.id)
            .gte('created_at', start.toISOString())
            .lte('created_at', end.toISOString());

          const completedTasks = tasks?.filter(t => t.completed) || [];

          return {
            weekRange: {
              start: format(start, 'MMMM d'),
              end: format(end, 'MMMM d, yyyy'),
              rawStart: start,
              rawEnd: end
            },
            stats: {
              totalJournalEntries: journalEntries?.length || 0,
              totalLessons: lessons?.length || 0,
              completedActions: completedTasks.length,
              totalActions: tasks?.length || 0,
              actionCompletionRate: tasks?.length ? 
                (completedTasks.length / tasks.length) * 100 : 0
            },
            entries: journalEntries,
            lessons: lessons,
            tasks: tasks
          };
        })
      );

      setSummaries(weeklyData);
    } catch (error: any) {
      console.error('Error fetching summaries:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetAnalysis = async (weekData: any, index: number) => {
    setLoadingIndex(index);
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    if (!userId) {
      console.error("User not authenticated");
      setLoadingIndex(null);
      return;
    }

    try {
      const response = await fetch('https://cdnbjnapvlbmarhuflsv.supabase.co/functions/v1/weekly-insights', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          userId,
          startDate: weekData.weekRange.rawStart,
          endDate: weekData.weekRange.rawEnd
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to generate insights');

      navigate('/weekly-insights', { state: result });
    } catch (error) {
      console.error('Error calling weekly-insights:', error);
    } finally {
      setLoadingIndex(null);
    }
  };

  const firstSummaryDate = signupDate ? format(endOfWeek(signupDate), 'MMMM d, yyyy') : '';
  const now = new Date();

  return (
      <ProtectedLayout>
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <BarChart2 className="w-8 h-8 text-[#236caf]" />
            <h1 className="text-3xl font-bold text-[#004060]">Weekly Summaries</h1>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>
        )}

        {summaries.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <h2 className="text-xl font-semibold text-[#004060] mb-4">
              Welcome to Weekly Summaries!
            </h2>
            <p className="text-gray-600 mb-4">
              Your first weekly summary will be automatically generated on {firstSummaryDate}. Keep journaling and completing tasks to see your progress over time.
            </p>
            <Button onClick={() => navigate('/journal/new')}>Start Journaling</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {summaries.map((summary, index) => {
              const weekId = `week-${index}`;
              const isExpanded = expandedWeek === weekId;
              const isWeekComplete = isBefore(summary.weekRange.rawEnd, now);

              return (
              
                <div key={weekId} className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <button
                    onClick={() => toggleWeek(weekId)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
                  >
                    <div>
                      <h2 className="text-lg font-semibold text-[#004060]">
                        {summary.weekRange.start} - {summary.weekRange.end}
                      </h2>
                      <p className="text-sm text-gray-600">
                        {summary.stats.totalJournalEntries} journal entries • {summary.stats.totalLessons} lessons learned • {summary.stats.completedActions} meaningful actions completed
                      </p>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="p-4 border-t">
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <BookOpen className="w-5 h-5 text-[#236caf]" />
                          <p className="text-2xl font-bold text-[#004060]">
                            {summary.stats.totalJournalEntries}
                          </p>
                        </div>

                        <div className="p-4 bg-orange-50 rounded-lg">
                          <BookOpen className="w-5 h-5 text-[#236caf]" />
                          <p className="text-2xl font-bold text-[#004060]">
                            {summary.stats.totalLessons}
                          </p>
                        </div>

                        <div className="p-4 bg-green-50 rounded-lg">
                          <CheckSquare className="w-5 h-5 text-[#236caf]" />
                          <p className="text-2xl font-bold text-[#004060]">
                            {summary.stats.completedActions}/{summary.stats.totalActions}
                          </p>
                        </div>

                        <div className="p-4 bg-purple-50 rounded-lg">
                          <p className="font-medium text-gray-700">Completion Rate</p>
                          <p className="text-2xl font-bold text-[#004060]">
                            {Math.round(summary.stats.actionCompletionRate)}%
                          </p>
                        </div>
                      </div>

                      {isWeekComplete ? (
                        <Button
                          onClick={() => handleGetAnalysis(summary, index)}
                          disabled={loadingIndex === index}
                          className="w-full mt-4 flex items-center justify-center gap-2"
                        >
                          {loadingIndex === index && (
                            <svg
                              className="h-4 w-4 animate-spin text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                              />
                            </svg>
                          )}
                          {loadingIndex === index ? 'Generating...' : 'Get Detailed Analysis'}
                        </Button>
                      ) : (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                          <p className="text-gray-700">
                            Your detailed analysis for this week will be available on {format(summary.weekRange.rawEnd, 'EEEE')}.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
    </ProtectedLayout>
  );
}
